import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Trash2, CalendarClock, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { apiRequest } from "@/lib/queryClient";
import { asArray } from "@/lib/utils";

interface AgentSchedule {
  id: number;
  academyId: number;
  batchId: number | null;
  jobType: string;
  cadence: string;
  enabled: number | boolean;
  lastRunAt: string | null;
  nextRunAt: string;
  createdByUserId: number;
  createdAt: string;
}

interface Batch {
  id: number;
  name: string;
}

const CADENCE_LABELS: Record<string, string> = {
  daily: "Daily",
  weekly_mon: "Weekly (Mon)",
  weekly_fri: "Weekly (Fri)",
  weekly_sun: "Weekly (Sun)",
};

function cadenceBadge(cadence: string) {
  return (
    <Badge className="bg-violet-100 text-violet-800 border-violet-200 border text-xs">
      {CADENCE_LABELS[cadence] ?? cadence}
    </Badge>
  );
}

function fmtDate(s: string | null): string {
  if (!s) return "—";
  return new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function AgentSchedulesPage() {
  const queryClient = useQueryClient();
  const [batchId, setBatchId] = useState<string>("");
  const [cadence, setCadence] = useState<string>("weekly_mon");

  const { data: schedules, isLoading } = useQuery<AgentSchedule[]>({
    queryKey: ["agent-schedules"],
    queryFn: async () => {
      const res: any = await apiRequest("GET", "/api/agents/schedules");
      return asArray<AgentSchedule>(res.data);
    },
  });

  const { data: batches } = useQuery<Batch[]>({
    queryKey: ["batches"],
    queryFn: async () => {
      const res: any = await apiRequest("GET", "/api/batches");
      return asArray<Batch>(res.batches ?? res.data);
    },
  });

  const createMutation = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/agents/schedules", {
        batchId: batchId ? Number(batchId) : null,
        cadence,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-schedules"] });
      setBatchId("");
      setCadence("weekly_mon");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      apiRequest("DELETE", `/api/agents/schedules/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["agent-schedules"] }),
  });

  const rows = schedules ?? [];

  return (
    <div
      className="px-4 py-6 md:px-6 md:py-8 max-w-3xl mx-auto space-y-6"
      data-testid="agent-schedules-page"
    >
      <div>
        <h1 className="text-2xl font-bold text-gray-900" data-testid="agent-schedules-heading">
          Recurring Agent Jobs
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Automatically run progress summaries on a schedule. Summaries go to the AI Task Queue for review.
        </p>
      </div>

      {/* Add schedule form */}
      <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm space-y-4">
        <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <Plus className="h-4 w-4 text-violet-500" />
          Add schedule
        </h2>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[160px]">
            <label className="block text-xs text-gray-500 mb-1">Squad (leave blank for all)</label>
            <select
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-violet-500"
              value={batchId}
              onChange={(e) => setBatchId(e.target.value)}
              data-testid="select-batch"
            >
              <option value="">All squads</option>
              {(batches ?? []).map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[140px]">
            <label className="block text-xs text-gray-500 mb-1">Cadence</label>
            <select
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-violet-500"
              value={cadence}
              onChange={(e) => setCadence(e.target.value)}
              data-testid="select-cadence"
            >
              {Object.entries(CADENCE_LABELS).map(([v, label]) => (
                <option key={v} value={v}>{label}</option>
              ))}
            </select>
          </div>
          <Button
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending}
            className="bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="btn-add-schedule"
          >
            Add
          </Button>
        </div>
      </div>

      {/* Schedule list */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => <Skeleton key={i} className="h-20 w-full rounded-xl" />)}
        </div>
      ) : rows.length === 0 ? (
        <div
          className="text-center py-16 text-gray-400"
          data-testid="schedules-empty"
        >
          <CalendarClock className="h-8 w-8 mx-auto mb-3 text-gray-300" />
          <p className="font-medium text-gray-500">No recurring jobs set up.</p>
          <p className="text-sm mt-1">Add one to run progress summaries automatically.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="schedules-list">
          {rows.map((s) => {
            const batchLabel = batches?.find((b) => b.id === s.batchId)?.name ?? "All squads";
            return (
              <div
                key={s.id}
                className="bg-white border border-gray-100 rounded-xl px-5 py-4 shadow-sm flex items-center justify-between gap-4 flex-wrap"
                data-testid={`schedule-row-${s.id}`}
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <CalendarClock className="h-4 w-4 text-violet-500 shrink-0" />
                    <span className="text-sm font-semibold text-gray-800">{batchLabel}</span>
                    {cadenceBadge(s.cadence)}
                  </div>
                  <p className="text-xs text-gray-400">
                    Last run: {fmtDate(s.lastRunAt)} · Next: {fmtDate(s.nextRunAt)}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50 shrink-0"
                  disabled={deleteMutation.isPending}
                  onClick={() => deleteMutation.mutate(s.id)}
                  data-testid={`btn-delete-schedule-${s.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" />
                  Remove
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
